import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    useTheme,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Alert,
    AlertTitle,
    Chip,
    Tabs,
    Tab,
    CircularProgress,
    LinearProgress,
    Tooltip,
    Divider,
    Stack,
    Badge
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    ArrowBack as ArrowBackIcon,
    Timeline as TimelineIcon,
    TrendingUp as TrendingUpIcon,
    TrendingDown as TrendingDownIcon,
    Warning as WarningIcon,
    Group as GroupIcon,
    AttachMoney as MoneyIcon,
    Notifications as NotificationsIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Project, ProjectSimulation, ProjectStatus } from '../../types/project';
import { projectService } from '../../services/projectService';
import { formatCurrency } from '../../utils/format';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import ProjectProposalDetails from './ProjectProposalDetails';
import { alpha } from '@mui/material/styles';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip as RechartsTooltip
} from 'recharts';
import ProjectVacancies from './ProjectVacancies';
import ProjectPositions from './ProjectPositions';
import ExecutionPanel from './execution/ExecutionPanel';
import { formatDateBR, parseLocalDate } from '../../utils/date';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`project-tabpanel-${index}`}
            aria-labelledby={`project-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

interface MonthlyData {
    month: string;
    planned: number;
    actual: number;
    plannedMargin: number;
    actualMargin: number;
}

interface AlertItem {
    type: 'warning' | 'error' | 'info' | 'success';
    title: string;
    message: string;
    action?: () => void;
    actionLabel?: string;
}

const ProjectDetails: React.FC = () => {
    const theme = useTheme();
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedSimulation, setSelectedSimulation] = useState<ProjectSimulation | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({ open: false, message: '', severity: 'success' });
    const [tabValue, setTabValue] = useState(0);
    const [showAlerts, setShowAlerts] = useState(false);

    const formatDate = (dateString: string | null | undefined) => formatDateBR(dateString);

    useEffect(() => {
        if (id) {
            loadProject();
        }
    }, [id]);

    const loadProject = async () => {
        try {
            setLoading(true);
            const data = await projectService.getProject(Number(id));
            
            // Garante que simulations seja sempre um array
            if (!data.simulations) {
                data.simulations = [];
            }
            
            // Atualiza o projeto com os dados carregados
            setProject({
                ...data,
                simulations: Array.isArray(data.simulations) ? data.simulations : []
            });
        } catch (error) {
            console.error('Erro ao carregar projeto:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao carregar projeto',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        navigate('/gestor/projetos');
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    if (loading || !project) {
        return (
            <Box p={3}>
                <Typography>Carregando projeto...</Typography>
            </Box>
        );
    }

    // Componente para a visão de Oportunidade
    const OpportunityView = () => {
    const handleCreateSimulation = () => {
        if (id) {
            navigate(`/gestor/projetos/${id}/simulacao/nova`);
        }
    };

    const handleEditSimulation = (simulationId: number) => {
        if (id) {
            navigate(`/gestor/projetos/${id}/simulacao/${simulationId}`);
        }
    };

    const handleDeleteSimulation = (simulation: ProjectSimulation) => {
        setSelectedSimulation(simulation);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (selectedSimulation?.id && id) {
            try {
                await projectService.deleteProjectSimulation(Number(id), selectedSimulation.id);
                loadProject();
            } catch (error) {
                console.error('Erro ao excluir simulação:', error);
            }
            setDeleteDialogOpen(false);
            setSelectedSimulation(null);
        }
    };

    const handleSelectSimulation = async (simulationId: number) => {
        if (id) {
            try {
                await projectService.selectProjectSimulation(Number(id), simulationId);
                loadProject();
            } catch (error) {
                console.error('Erro ao selecionar simulação:', error);
            }
        }
    };

        return (
            <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={handleBack} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4">
                        {project.name}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={handleCreateSimulation}
                >
                    Nova Simulação
                </Button>
            </Box>

            {/* Informações do Projeto */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Informações Gerais
                            </Typography>
                            <Typography color="textSecondary">
                                Cliente: {project.client?.name}
                            </Typography>
                            <Typography color="textSecondary">
                                {t('manager.projects.fields.status')}: {project.statusLabel}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
            </Grid>

            {/* Lista de Simulações */}
            <Box mb={4}>
                <Typography variant="h5" gutterBottom>
                    Simulações ({project.simulations?.length || 0})
                </Typography>
                {project.simulations && project.simulations.length > 0 ? (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>Receita Total</TableCell>
                                    <TableCell>Custos Totais</TableCell>
                                    <TableCell>Lucro Total</TableCell>
                                    <TableCell>Margem</TableCell>
                                    <TableCell align="right">Ações</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {project.simulations.map((simulation) => (
                                    <TableRow 
                                        key={simulation.id}
                                        selected={simulation.id === project.selectedSimulationId}
                                        sx={{
                                            backgroundColor: simulation.rejection_reason 
                                                ? alpha(theme.palette.error.light, 0.05) 
                                                : 'inherit'
                                        }}
                                    >
                                        <TableCell>
                                            <Box>
                                                <Typography>{simulation.name}</Typography>
                                                {simulation.rejection_reason && (
                                                    <Typography 
                                                        variant="caption" 
                                                        color="error"
                                                        sx={{ display: 'block', mt: 0.5 }}
                                                    >
                                                        Motivo da rejeição: {simulation.rejection_reason}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </TableCell>
                                        <TableCell>{formatCurrency(simulation.total_revenue)}</TableCell>
                                        <TableCell>{formatCurrency(simulation.total_costs)}</TableCell>
                                        <TableCell>{formatCurrency(simulation.total_profit)}</TableCell>
                                        <TableCell>{simulation.profit_margin.toFixed(2)}%</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                size="small"
                                                onClick={() => simulation.id && handleEditSimulation(simulation.id)}
                                                color="primary"
                                                disabled={!!simulation.rejection_reason}
                                                sx={{ opacity: simulation.rejection_reason ? 0.5 : 1 }}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => simulation.id && handleSelectSimulation(simulation.id)}
                                                color="success"
                                                disabled={simulation.id === project.selectedSimulationId || !!simulation.rejection_reason}
                                                sx={{ opacity: (simulation.id === project.selectedSimulationId || simulation.rejection_reason) ? 0.5 : 1 }}
                                            >
                                                <CheckIcon />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteSimulation(simulation)}
                                                color="error"
                                                disabled={!!simulation.rejection_reason}
                                                sx={{ opacity: simulation.rejection_reason ? 0.5 : 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Typography color="textSecondary">
                        Nenhuma simulação criada ainda.
                    </Typography>
                )}
            </Box>

            {/* Diálogo de Confirmação de Exclusão */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
            >
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir esta simulação?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteConfirm} color="error">
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
            </Box>
        );
    };

    // Componente para a visão de Projeto Ativo
    const ActiveProjectView = () => {
        const [selectedPosition, setSelectedPosition] = useState<any>(null);
        const [openVacancyDialog, setOpenVacancyDialog] = useState(false);
        const [showAlerts, setShowAlerts] = useState(false);
        // Custos reais lançados por posição (antes o gráfico usava Math.random())
        const [actualCostsByMonth, setActualCostsByMonth] = useState<Record<string, number>>({});

        useEffect(() => {
            projectService.getProjectCosts(project.id)
                .then((data) => {
                    const byMonth: Record<string, number> = {};
                    (data.positions || []).forEach((pos) => (pos.actual_costs || []).forEach((c) => {
                        const key = String(c.month).slice(0, 7);
                        byMonth[key] = (byMonth[key] || 0) + Number(c.cost || 0);
                    }));
                    setActualCostsByMonth(byMonth);
                })
                .catch(() => setActualCostsByMonth({}));
        }, []);

        // Série mensal: planejado (simulação aprovada) x real (custos lançados) — sem valores inventados
        const generateMonthlyCosts = (): (MonthlyData & { hasActual: boolean })[] => {
            const sim = project.simulation;
            const start = parseLocalDate(sim?.start_date);
            if (!sim || !start || !sim.duration_months) return [];
            const plannedCost = sim.total_costs / sim.duration_months;
            const plannedRevenue = sim.total_revenue / sim.duration_months;
            const plannedMargin = plannedRevenue ? ((plannedRevenue - plannedCost) / plannedRevenue) * 100 : 0;
            return Array.from({ length: sim.duration_months }, (_, i) => {
                const month = new Date(start.getFullYear(), start.getMonth() + i, 1);
                const key = format(month, 'yyyy-MM');
                const actual = actualCostsByMonth[key];
                const hasActual = actual !== undefined;
                return {
                    month: format(month, 'MMM/yy', { locale: ptBR }),
                    planned: plannedCost,
                    actual: hasActual ? actual : (null as unknown as number),
                    plannedMargin,
                    actualMargin: hasActual && plannedRevenue
                        ? ((plannedRevenue - actual) / plannedRevenue) * 100
                        : (null as unknown as number),
                    hasActual,
                };
            });
        };

        const handleOpenVacancy = async (positionId: number) => {
            try {
                // Recarrega o projeto para atualizar todas as informações
                await loadProject();
            } catch (error) {
                console.error('Error updating project after opening vacancy:', error);
                setSnackbar({
                    open: true,
                    message: 'Erro ao atualizar projeto após abrir vaga',
                    severity: 'error'
                });
            }
        };

        const handleCreateVacancy = async () => {
            if (!selectedPosition) return;

            try {
                setLoading(true);
                await projectService.openPositionVacancy(project.id, selectedPosition.id, {
                    description: '',  // Will be filled in the vacancy form
                    requirements: ''  // Will be filled in the vacancy form
                });
                loadProject();
                setOpenVacancyDialog(false);
            } catch (error) {
                console.error('Error opening vacancy:', error);
            } finally {
                setLoading(false);
            }
        };

        // Calcula KPIs
        const calculateKPIs = () => {
            if (!project.simulation) return null;

            const totalPositions = project.simulation.positions.length;
            const filledPositions = project.simulation.positions.filter(p => p.filled_by_user_id).length;
            const openPositions = project.simulation.positions.filter(p => p.status === 'OPEN').length;
            const plannedPositions = project.simulation.positions.filter(p => p.status === 'PLANNED').length;

            return {
                totalPositions,
                filledPositions,
                openPositions,
                plannedPositions,
                occupationRate: totalPositions ? (filledPositions / totalPositions) * 100 : 0
            };
        };

        const kpis = calculateKPIs();
        const monthlyCosts = generateMonthlyCosts();

        // Dados para o gráfico de pizza de status das posições
        const positionStatusData = [
            { name: 'Preenchidas', value: kpis?.filledPositions || 0 },
            { name: 'Vagas Abertas', value: kpis?.openPositions || 0 },
            { name: 'Planejadas', value: kpis?.plannedPositions || 0 }
        ];

        const COLORS = ['#4caf50', '#ff9800', '#2196f3'];

        // Alertas derivados dos dados reais (antes eram textos fixos, iguais para todo projeto)
        const monthsOverBudget = monthlyCosts.filter((m) => m.hasActual && m.actual > m.planned);
        const unfilledPositions = kpis ? kpis.totalPositions - kpis.filledPositions : 0;
        const projectAlerts: AlertItem[] = [
            ...(unfilledPositions > 0 ? [{
                type: 'warning' as const,
                title: 'Posições sem pessoa alocada',
                message: `${unfilledPositions} de ${kpis!.totalPositions} posições ainda não foram preenchidas.`,
                action: () => { setShowAlerts(false); setTabValue(2); },
                actionLabel: 'Ver equipe',
            }] : []),
            ...(monthsOverBudget.length > 0 ? [{
                type: 'error' as const,
                title: 'Custo acima do planejado',
                message: `Custo real acima do planejado em: ${monthsOverBudget.map((m) => m.month).join(', ')}.`,
                action: () => { setShowAlerts(false); setTabValue(1); },
                actionLabel: 'Ver financeiro',
            }] : []),
        ];

        // Tendência só com meses que têm custo real lançado
        const calculateTrends = () => {
            const withActual = monthlyCosts.filter((m) => m.hasActual).slice(-3);
            if (withActual.length === 0) return null;
            const marginTrend = withActual.reduce((acc, curr) => acc + (curr.actualMargin - curr.plannedMargin), 0) / withActual.length;
            const costTrend = withActual.reduce((acc, curr) => acc + (curr.actual - curr.planned), 0) / withActual.length;
            return { marginTrend, costTrend };
        };

        const trends = calculateTrends();

        return (
            <Box>
                {/* Header com Alertas */}
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={1}>
                    <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" minWidth={0}>
                        <IconButton onClick={handleBack} color="primary" aria-label="Voltar">
                            <ArrowBackIcon />
                        </IconButton>
                        <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' }, overflowWrap: 'anywhere' }}>
                            {project.name}
                        </Typography>
                        <Chip
                            label={project.client?.name}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Tooltip title={projectAlerts.length ? 'Ver alertas' : 'Nenhum alerta'}>
                            <IconButton onClick={() => setShowAlerts(true)} aria-label="Alertas do projeto">
                                <Badge badgeContent={projectAlerts.length} color="error">
                                    <NotificationsIcon />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Cards de KPIs com Tendências */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: '100%', bgcolor: theme.palette.primary.light }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography variant="h6" gutterBottom color="white">
                                        Taxa de Ocupação
                                    </Typography>
                                    {trends && (trends.marginTrend >= 0 ?
                                        <TrendingUpIcon color="success" /> :
                                        <TrendingDownIcon color="error" />
                                    )}
                                </Box>
                                <Typography variant="h3" color="white">
                                    {kpis && kpis.totalPositions ? `${Math.round(kpis.occupationRate)}%` : '0%'}
                                </Typography>
                                <Typography color="white" variant="subtitle2">
                                    {kpis ? `${kpis.filledPositions}/${kpis.totalPositions} posições` : '0/0 posições'}
                                </Typography>
                                {trends && (
                                    <Box mt={1}>
                                        <Tooltip title="Margem real x planejada nos últimos meses com custo lançado">
                                            <Typography color="white" variant="caption">
                                                {trends.marginTrend > 0 ? '+' : ''}{trends.marginTrend.toFixed(1)} p.p. de margem vs planejado
                                            </Typography>
                                        </Tooltip>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: '100%', bgcolor: theme.palette.success.light }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="white">
                                    Posições Preenchidas
                                </Typography>
                                <Typography variant="h3" color="white">
                                    {kpis?.filledPositions || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: '100%', bgcolor: theme.palette.warning.light }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="white">
                                    Vagas Abertas
                                </Typography>
                                <Typography variant="h3" color="white">
                                    {kpis?.openPositions || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Card sx={{ height: '100%', bgcolor: theme.palette.info.light }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom color="white">
                                    Posições Planejadas
                                </Typography>
                                <Typography variant="h3" color="white">
                                    {kpis?.plannedPositions || 0}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* A "Timeline do Projeto" fixa ("Entrega MVP em 15 dias") foi removida: o cronograma real está na aba Execução */}

                {/* Gráficos em Tabs para melhor organização */}
                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={tabValue} onChange={(event, newValue) => setTabValue(newValue)}>
                                <Tab label="Execução" icon={<TimelineIcon />} />
                                <Tab label="Financeiro" icon={<MoneyIcon />} />
                                <Tab label="Equipe" icon={<GroupIcon />} />
                                <Tab label="Previsões" icon={<TrendingUpIcon />} />
                            </Tabs>
                        </Box>
                        <Box sx={{ pt: 2 }}>
                            {tabValue === 0 && <ExecutionPanel projectId={project.id} />}
                            {tabValue === 1 && (
                                <Grid container spacing={3}>
                                    {/* Gráficos financeiros */}
                                    <Grid item xs={12} md={8}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Custo planejado x real (mensal)
                                                </Typography>
                                                {!trends && (
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Nenhum custo real lançado ainda — a linha "Custo Real" aparece conforme os custos das posições forem registrados.
                                                    </Typography>
                                                )}
                                                <Box sx={{ height: 300 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={monthlyCosts}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="month" />
                                                            <YAxis />
                                                            <RechartsTooltip 
                                                                formatter={(value) => formatCurrency(Number(value))}
                                                                labelFormatter={(label) => `Mês: ${label}`}
                                                            />
                                                            <Legend />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="planned"
                                                                name="Custo Planejado"
                                                                stroke={theme.palette.primary.main}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="actual"
                                                                name="Custo Real"
                                                                stroke={theme.palette.error.main}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Distribuição de Custos
                                                </Typography>
                                                <Box sx={{ height: 300 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <PieChart>
                                                            <Pie
                                                                data={positionStatusData}
                                                                cx="50%"
                                                                cy="50%"
                                                                labelLine={false}
                                                                label={({ name, percent }) => {
                                                                    const pct = percent ?? 0;
                                                                    return `${name} (${(pct * 100).toFixed(0)}%)`;
                                                                  }}                                                                  
                                                                outerRadius={80}
                                                                fill="#8884d8"
                                                                dataKey="value"
                                                            >
                                                                {positionStatusData.map((entry, index) => (
                                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                                ))}
                                                            </Pie>
                                                            <RechartsTooltip 
                                                                formatter={(value, name) => [formatCurrency(Number(value)), name]}
                                                            />
                                                        </PieChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                            {tabValue === 2 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <ProjectPositions
                                            projectId={project.id}
                                            positions={project.simulation?.positions || []}
                                            onOpenVacancy={handleOpenVacancy}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                            {tabValue === 3 && (
                                <Grid container spacing={3}>
                                    {/* Gráficos de previsão */}
                                    <Grid item xs={12} md={6}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Previsão de Margem
                                                </Typography>
                                                <Box sx={{ height: 300 }}>
                                                    <ResponsiveContainer width="100%" height="100%">
                                                        <LineChart data={monthlyCosts}>
                                                            <CartesianGrid strokeDasharray="3 3" />
                                                            <XAxis dataKey="month" />
                                                            <YAxis unit="%" />
                                                            <RechartsTooltip 
                                                                formatter={(value) => `${Number(value).toFixed(2)}%`}
                                                                labelFormatter={(label) => `Mês: ${label}`}
                                                            />
                                                            <Legend />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="plannedMargin"
                                                                name="Margem Planejada"
                                                                stroke={theme.palette.success.main}
                                                            />
                                                            <Line
                                                                type="monotone"
                                                                dataKey="actualMargin"
                                                                name="Margem Real"
                                                                stroke={theme.palette.warning.main}
                                                            />
                                                        </LineChart>
                                                    </ResponsiveContainer>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6" gutterBottom>
                                                    Alertas e Previsões
                                                </Typography>
                                                <Box sx={{ p: 2 }}>
                                                    {!trends && (
                                                        <Alert severity="info" sx={{ mb: 2 }}>
                                                            Ainda não há custos reais lançados; as previsões usam apenas o planejado da simulação.
                                                        </Alert>
                                                    )}
                                                    {trends && trends.marginTrend < 0 && (
                                                        <Alert severity="warning" sx={{ mb: 2 }}>
                                                            <AlertTitle>Tendência de Queda na Margem</AlertTitle>
                                                            A margem real está {Math.abs(trends.marginTrend).toFixed(1)} p.p. abaixo do planejado.
                                                            Recomenda-se revisar os custos do projeto.
                                                        </Alert>
                                                    )}
                                                    {kpis && kpis.totalPositions > 0 && kpis.occupationRate < 80 && (
                                                        <Alert severity="info" sx={{ mb: 2 }}>
                                                            <AlertTitle>Ocupação Abaixo do Ideal</AlertTitle>
                                                            A taxa de ocupação atual é de {Math.round(kpis.occupationRate)}%.
                                                            Considere acelerar o processo de contratação.
                                                        </Alert>
                                                    )}
                                                    {monthsOverBudget.length > 0 && (
                                                        <Alert severity="error">
                                                            <AlertTitle>Custos Acima do Planejado</AlertTitle>
                                                            Custo real acima do planejado em: {monthsOverBudget.map((m) => m.month).join(', ')}.
                                                            Avalie medidas de contenção de custos.
                                                        </Alert>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            )}
                        </Box>
                    </CardContent>
                </Card>

                {/* Dialog de Alertas */}
                <Dialog
                    open={showAlerts}
                    onClose={() => setShowAlerts(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        Alertas do Projeto
                        <IconButton
                            onClick={() => setShowAlerts(false)}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            {projectAlerts.length === 0 && (
                                <Typography color="text.secondary">Nenhum alerta no momento.</Typography>
                            )}
                            {projectAlerts.map((alert, index) => (
                                // Ação abaixo do texto: ao lado, no celular, espremia o conteúdo
                                <Alert key={index} severity={alert.type}>
                                    <AlertTitle>{alert.title}</AlertTitle>
                                    {alert.message}
                                    {alert.action && (
                                        <Box mt={1}>
                                            <Button color="inherit" size="small" variant="outlined" onClick={alert.action}>
                                                {alert.actionLabel}
                                            </Button>
                                        </Box>
                                    )}
                                </Alert>
                            ))}
                        </Stack>
                    </DialogContent>
                </Dialog>
            </Box>
        );
    };

    // Renderiza a visão apropriada baseada no status do projeto
    switch (project.status) {
        case ProjectStatus.PROPOSAL:
            return (
                <ProjectProposalDetails
                    project={project}
                    onBack={handleBack}
                    onReload={loadProject}
                />
            );
        case ProjectStatus.ACTIVE:
            return <ActiveProjectView />;
        default: // OPPORTUNITY
            return <OpportunityView />;
    }

    return (
        <Box>
            {/* Snackbar para mensagens */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {loading && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 9999
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </Box>
    );
};

export default ProjectDetails; 